import { ROUTE_LABELS } from "@/B2C/features/lovable/utils";
import { App_Url_New } from "@/shared/common/static";
import CommonLogoutModal from "@/shared/components/CommonLogoutModal";
import { UserAccountMenu } from "@/shared/components/UserAccountMenu";
import { useOpenChatbot } from "@/shared/hooks/useOpenChatbot";
import { axiosInstance } from "@/shared/lib/axiosInstance";
import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { useSecureAccess } from "@/shared/permissionBase";
import { setMemberList } from "@/shared/redux/reducers/masterSlice";
import { openModal } from "@/shared/redux/reducers/uiSlice";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { App_Url, getChatbotName } from "@/whiteLable";
import { ArrowLeft, Bell, LogOut, Search, Settings, UserRound } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

export default function DashboardHeader(): React.JSX.Element {
  const pageShellClasses = getThemeComponentGroup("pageShell");
  const cardClasses = getThemeComponentGroup("card");
  const {
    auth: { user, session },
    masterSlice: { memberList },
    ui: { name: activeModalName, status: activeModalStatus },
  } = usePosterReducers();

  const { pathname, search } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState("");
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const notificationDropdownRef = useRef<HTMLDivElement>(null);
  const { openChatbot } = useOpenChatbot();
  const isChatbotOpen =
    activeModalName === App_Url_New.PopupKeys.OPEN_CHAT_BOT && activeModalStatus !== "hide";
  const chatbotName = getChatbotName();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
        setHeaderSearchQuery("");
      }

      if (
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(event.target as Node)
      ) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const canGoBack = window.history.state?.idx > 0;
  const normalizedPathname =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const normalizedEditQuotePath =
    App_Url_New.B2CPageUrl.editQuote.length > 1 && App_Url_New.B2CPageUrl.editQuote.endsWith("/")
      ? App_Url_New.B2CPageUrl.editQuote.slice(0, -1)
      : App_Url_New.B2CPageUrl.editQuote;
  // Only modules the backend grants the "view" action for are part of nav/search.
  const { navItems: secureNavItems } = useSecureAccess();
  const secureRootPaths = useMemo(
    () =>
      secureNavItems
        .map((item) => item.path)
        .filter((path): path is string => Boolean(path))
        .map((path) => (path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path)),
    [secureNavItems]
  );
  const shouldHideBackButton = secureRootPaths.includes(normalizedPathname);
  const shouldShowBackButton = canGoBack && !shouldHideBackButton;

  const permittedNavItems = secureNavItems;

  const searchResults = useMemo(() => {
    const query = headerSearchQuery.trim().toLowerCase();

    if (!query) {
      return permittedNavItems;
    }

    return permittedNavItems.filter((item) => item.label.toLowerCase().includes(query));
  }, [headerSearchQuery, permittedNavItems]);

  const handleSearchNavigation = (path: string) => {
    navigate(path);
    setHeaderSearchQuery("");
    setIsSearchOpen(false);
  };

  const isEditQuoteRoute = normalizedPathname === normalizedEditQuotePath;

  useEffect(() => {
    if (!isEditQuoteRoute) return;

    let isMounted = true;

    const fetchTeamMembers = async () => {
      try {
        const response = await axiosInstance.post(App_Url_New.ApiEndPoint.teamMembers, {
          page: 1,
          page_size: 5000,
          search: "",
          ordering: null,
          filters: {},
        });

        if (!isMounted) return;
        dispatch(setMemberList(response?.data));
      } catch (error) {
        console.error("Failed to fetch team members on edit quote page:", error);
      }
    };

    fetchTeamMembers();

    return () => {
      isMounted = false;
    };
  }, [dispatch, isEditQuoteRoute]);

  const handleHeaderBack = () => {
    if (isEditQuoteRoute) {
      navigate(App_Url_New.B2CPageUrl.quotes, { replace: true });
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(App_Url_New.B2CPageUrl.quotes);
  };

  const formatPageLabel = (value: string): string => {
    return decodeURIComponent(value)
      .replace(/[-_]+/g, " ")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getPageTitle = (path) => {
    const cleanPath = path.split("?")[0].split("#")[0];
    if (cleanPath === App_Url_New.CommonPageUrl.profile) return "My Profile";
    const segments = cleanPath.split("/").filter(Boolean);

    // For Lovable iframe routes, show the section name (side nav label) instead of the route.
    const firstSegment = segments[0] ?? "";
    if (ROUTE_LABELS[firstSegment]) {
      return ROUTE_LABELS[firstSegment];
    }

    const queryParams = new URLSearchParams(search);
    const nameParam = queryParams.get("name");

    if (nameParam) {
      return formatPageLabel(nameParam);
    }

    const lastSegment = segments[segments.length - 1] || "dashboard";
    return formatPageLabel(lastSegment);
  };

  const title = getPageTitle(pathname);
  const editQuoteId = useMemo(() => {
    if (!isEditQuoteRoute) {
      return "";
    }

    const queryParams = new URLSearchParams(search);
    const quoteId = queryParams.get("quoteId")?.trim();

    return quoteId ? `QT${quoteId}` : "";
  }, [isEditQuoteRoute, search]);
  const assignedAgentName = useMemo(() => {
    const sessionQuote = session?.session_quotes;
    const nameFromPayload =
      sessionQuote?.assigned_agent_name ||
      sessionQuote?.assign_agent_name ||
      sessionQuote?.user?.full_name ||
      sessionQuote?.assigned_user?.full_name ||
      sessionQuote?.assign_user?.full_name ||
      "";

    if (nameFromPayload) {
      return nameFromPayload;
    }

    const assignedId =
      sessionQuote?.assign_agent_id ??
      sessionQuote?.assigned_agent_id ??
      sessionQuote?.assigned_user_id ??
      sessionQuote?.assign_user_id ??
      sessionQuote?.assigned_user?.id ??
      sessionQuote?.assign_user?.id ??
      sessionQuote?.user?.id;

    if (!assignedId) {
      return "";
    }

    const candidates: any[] = [
      ...(Array.isArray(memberList) ? memberList : []),
      ...(Array.isArray(session?.users) ? session.users : []),
      ...(Array.isArray(session?.members) ? session.members : []),
    ];

    const matchedCandidate = candidates.find((candidate: any) => {
      const candidateId =
        candidate?.employee?.id ??
        candidate?.employee_id ??
        candidate?.employeeId ??
        candidate?.assigned_user_id ??
        candidate?.user_id ??
        candidate?.id;

      return String(candidateId) === String(assignedId);
    });

    return (
      matchedCandidate?.employee?.full_name ||
      matchedCandidate?.full_name ||
      matchedCandidate?.name ||
      ""
    );
  }, [memberList, session]);
  const effectiveTitle = isEditQuoteRoute
    ? assignedAgentName
      ? `Edit${editQuoteId ? ` | ${editQuoteId}` : ""} | Assigned To: ${assignedAgentName}`
      : `Edit${editQuoteId ? ` | ${editQuoteId}` : ""} | Agent Unassigned`
    : title;
  const getInitials = (fullName) => {
    if (!fullName) return "U";

    const names = fullName.trim().split(" ").filter(Boolean);

    if (names.length === 1) {
      return names[0][0].toUpperCase();
    }

    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  const userName = getInitials(user?.user?.full_name);

  const handleSignOut = () => {
    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.LOGOUT_CONFIRM,
        title: "Logout?",
        className: "max-w-md",
        children: <CommonLogoutModal displayName={userName} />,
      })
    );
  };

  return (
    <header
      className={`h-16 border-b flex items-center justify-between px-4 sticky top-0 z-80 ${getThemeClass(cardClasses.bgColor, "bg-theme-surface")} ${getThemeClass(pageShellClasses.borderColor, "border-theme-border/70")}`}
    >
      <div className="flex items-center gap-2">
        {/* {!isSidebarOpen && (
          <button onClick={onOpenSidebar} className="md:hidden p-2">
            <Menu size={20} />
          </button>
        )} */}
        {shouldShowBackButton && (
          <button
            type="button"
            onClick={handleHeaderBack}
            className={`inline-flex items-center gap-1 rounded-full p-2 text-sm ${getThemeClass(pageShellClasses.mutedTextColor, "text-theme-text-soft")} hover:bg-theme-accent/55`}
          >
            <ArrowLeft size={20} />
          </button>
        )}
        <h1
          className={`text-lg font-bold hidden sm:block ${getThemeClass(pageShellClasses.textColor, "text-theme-text")}`}
        >
          {effectiveTitle}
        </h1>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        <span
          role="button"
          aria-label={`Open Ask ${chatbotName}`}
          title={`Ask ${chatbotName}`}
          onClick={openChatbot}
          className={`inline-flex cursor-pointer select-none items-center gap-2 rounded-xl border px-3 py-1 text-sm font-semibold shadow-sm transition-colors ${
            isChatbotOpen
              ? "border-theme-primary bg-theme-primary/5 text-theme-primary ring-1 ring-theme-primary/20"
              : "border-slate-200 bg-white text-slate-800"
          }`}
        >
          <span
            className={`flex h-7 w-7 items-center justify-center rounded-full bg-linear-to-br ${
              isChatbotOpen
                ? "from-theme-primary/15 to-theme-primary/5"
                : "from-blue-50 to-indigo-50"
            }`}
          >
            <img
              src={App_Url.public.ChatBot}
              alt={chatbotName}
              className="h-5 w-5 object-contain"
            />
          </span>

          <span className="hidden sm:inline truncate text-sm font-semibold leading-4">
            Ask {chatbotName}
          </span>
        </span>
        <div ref={searchDropdownRef} className="relative hidden lg:block">
          <button
            type="button"
            aria-label="Search pages"
            onClick={() => {
              setIsSearchOpen((current) => !current);
              setIsNotificationOpen(false);
            }}
            className={`rounded-full p-2 ${getThemeClass(
              pageShellClasses.mutedTextColor,
              "text-theme-text-soft"
            )} hover:bg-theme-surface-muted`}
          >
            <Search size={20} />
          </button>

          {isSearchOpen && (
            <div
              className={`absolute right-0 top-full z-90 mt-2 w-72 overflow-hidden rounded-lg border shadow-lg ${getThemeClass(
                cardClasses.bgColor,
                "bg-theme-surface"
              )} ${getThemeClass(pageShellClasses.borderColor, "border-theme-border")}`}
            >
              <div className="relative border-b border-theme-border p-3">
                <Search
                  size={16}
                  className={`absolute left-6 top-1/2 -translate-y-1/2 ${getThemeClass(
                    pageShellClasses.mutedTextColor,
                    "text-theme-text-soft"
                  )}`}
                />

                <input
                  autoFocus
                  type="text"
                  placeholder="Search pages..."
                  value={headerSearchQuery}
                  onChange={(event) => setHeaderSearchQuery(event.target.value)}
                  className="w-full rounded-md border border-theme-border bg-theme-surface-muted py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-theme-accent"
                />
              </div>

              <div className="max-h-64 overflow-y-auto p-2">
                {searchResults.length > 0 ? (
                  searchResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        if (item.path) {
                          handleSearchNavigation(item.path);
                        }
                      }}
                      className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-left text-sm text-theme-text-muted hover:bg-theme-surface-soft"
                    >
                      <item.icon size={18} />
                      <span>{item.label}</span>
                    </button>
                  ))
                ) : (
                  <p className="px-3 py-6 text-center text-sm text-theme-text-soft">
                    No pages found
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <div ref={notificationDropdownRef} className="relative">
          <button
            type="button"
            aria-label="Notifications"
            onClick={() => {
              setIsNotificationOpen((current) => !current);
              setIsSearchOpen(false);
              setHeaderSearchQuery("");
            }}
            className={`relative rounded-full p-2 ${getThemeClass(
              pageShellClasses.mutedTextColor,
              "text-theme-text-soft"
            )} hover:bg-theme-surface-muted`}
          >
            <Bell size={20} />
          </button>

          {isNotificationOpen && (
            <div
              className={`absolute right-0 top-full z-90 mt-2 w-72 overflow-hidden rounded-lg border shadow-lg ${getThemeClass(
                cardClasses.bgColor,
                "bg-theme-surface"
              )} ${getThemeClass(pageShellClasses.borderColor, "border-theme-border")}`}
            >
              <div className="border-b border-theme-border px-4 py-3">
                <h2 className="text-sm font-semibold text-theme-text">Notifications</h2>
              </div>

              <div className="px-4 py-8 text-center">
                <Bell size={28} className="mx-auto text-theme-text-soft" />

                <p className="mt-3 text-sm font-medium text-theme-text">No notifications</p>

                <p className="mt-1 text-xs text-theme-text-soft">You&apos;re all caught up.</p>
              </div>
            </div>
          )}
        </div>
        <UserAccountMenu
          fullName={user?.user?.full_name}
          role={user?.user?.role?.display_name || user?.user?.role?.name}
          actions={[
            {
              label: "Profile",
              icon: <UserRound className="h-4 w-4" />,
              onClick: () => navigate(App_Url_New.CommonPageUrl.profile),
            },
            {
              label: "Settings",
              icon: <Settings className="h-4 w-4" />,
              onClick: () => navigate(App_Url_New.B2CPageUrl.settings),
            },
            {
              label: "Logout",
              icon: <LogOut className="h-4 w-4" />,
              danger: true,
              onClick: handleSignOut,
            },
          ]}
          email={user?.user?.email}
        />
      </div>
    </header>
  );
}
